import { PrudsysCategoryToBrandsPapiDocument } from '@packages/gql/generated/shopping/PrudsysCategoryToBrandsPapiDocument';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { getPapidata } from '../../../helpers/getPapiData';
import { useRecoElement } from '../../../helpers/useRecoElement';
import type { BrandsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoBrandCard } from '../../RecoBrandCard';
import { useBrandsRecoTracking } from '../../RecoBrandCard/useBrandsRecoTracking';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoCarouselSkeleton } from '../../RecoCarouselSkeleton';

export type PrudsysCategoryToBrandsPapiProps = Pick<RequestParams, 'categoryId'> &
  Omit<BrandsRecoSliderProps, 'preferTitle'>;

/* GraphQL */ `
  query PrudsysCategoryToBrandsPapi(
    $categoryId: String!
    $locale: String!
    $papibrands: [String!]!
  ) {
    prudsysCategoryToBrandsPapi(categoryId: $categoryId, locale: $locale, papibrands: $papibrands) {
      type
      recommendations {
        ...BrandRecommendationFragment
      }
    }
  }
`;

const RDE_TYPE: RdeType = 'prudsysCategoryToBrandsPapi';

/**
 * PrudsysCategoryToBrandsPapiRecoSlider component description displayed in storybook
 * */
export const PrudsysCategoryToBrandsPapiRecoSlider: FC<PrudsysCategoryToBrandsPapiProps> = ({
  categoryId,
  elementIndex = 0,
  fetchOnThreshold = false,
  pageTemplate,
  sourceCategoryId,
  title,
}) => {
  const { isInThreshold, isInView, recoEl } = useRecoElement();
  const { locale } = useIntl();

  const [{ data, error }] = useQuery({
    query: PrudsysCategoryToBrandsPapiDocument,
    variables: {
      categoryId,
      locale: locale!,
      papibrands: getPapidata('hotArticlesIntraday', []),
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.prudsysCategoryToBrandsPapi.recommendations : undefined;

  const rdeTypeAbbr = data?.prudsysCategoryToBrandsPapi.type;

  useBrandsRecoTracking({
    rdeTypeAbbr,
    elementIndex,
    trackingCondition: isInView && !!recommendations && recommendations.length > 0,
    sourceCategoryId,
  });

  if ((error && !recommendations) || (recommendations && recommendations.length === 0)) {
    return null;
  }

  return (
    <div ref={recoEl}>
      {!recommendations ? (
        <RecoCarouselSkeleton type="BRAND" />
      ) : (
        <RecoCarousel title={title} loadingState={!recommendations}>
          {recommendations.map((recommendation, idx) => (
            <RecoBrandCard
              pageTemplate={pageTemplate}
              // rule is disabled because order doesn't change between rerenders
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              position={idx}
              recommendationData={recommendation}
              brandsLength={recommendations.length}
              rdeType={RDE_TYPE}
            />
          ))}
        </RecoCarousel>
      )}
    </div>
  );
};
